import React from "react";
import {
    Building2,
    ExternalLink,
    DownloadCloud,
    Loader2,
    Hash,
    MapPin,
    Calendar as CalendarIcon,
    CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtEUR } from "@/lib/format";

const STATE_BADGES = {
    PUB: { label: "Publicada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    PRE: { label: "Anuncio previo", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    EV: { label: "En evaluación", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    EVL: { label: "En evaluación", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    RES: { label: "Resuelta", cls: "bg-ink-100 text-ink-700 border-ink-200" },
    ADJ: { label: "Adjudicada", cls: "bg-violet-50 text-violet-700 border-violet-200" },
    FORM: { label: "Formalizada", cls: "bg-violet-50 text-violet-700 border-violet-200" },
    ANUL: { label: "Anulada", cls: "bg-red-50 text-red-700 border-red-200" },
    DES: { label: "Desierta", cls: "bg-orange-50 text-orange-700 border-orange-200" },
};

export default function PlcspResultCard({
    r,
    isImported,
    isImporting,
    importingWithPdfs,
    onImport,
}) {
    const stBadge = STATE_BADGES[r.state_code] || {
        label: r.state_code || "—",
        cls: "bg-ink-50 text-ink-600 border-ink-100",
    };
    const rowId = r.expediente || r.external_id;
    return (
        <div
            className="group bg-white border border-ink-100 rounded-2xl p-5 shadow-pop hover:shadow-pop-lg hover:border-brand-200 transition-all"
            data-testid={`plcsp-row-${rowId}`}
        >
            <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span
                            className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${stBadge.cls}`}
                        >
                            {stBadge.label}
                        </span>
                        {r.procedure_type === "menor" && (
                            <span
                                className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border bg-amber-50 text-amber-700 border-amber-200"
                                data-testid="plcsp-badge-menor"
                            >
                                Contrato menor
                            </span>
                        )}
                        {r.ambito === "agregada" && (
                            <span
                                className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border bg-violet-50 text-violet-700 border-violet-200"
                                data-testid="plcsp-badge-agregada"
                            >
                                Autonómico · Local
                            </span>
                        )}
                        {r.expediente && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-ink-500 bg-ink-50 px-2 py-1 rounded-full">
                                <Hash size={10} />
                                {r.expediente}
                            </span>
                        )}
                        {(r.cpv || []).slice(0, 2).map((c, idx) => (
                            <span
                                key={`${c}-${idx}`}
                                className="text-[10px] font-mono font-bold text-brand-700 bg-brand-50 px-2 py-1 rounded-full"
                            >
                                CPV {c}
                            </span>
                        ))}
                    </div>
                    <h3 className="font-display text-lg md:text-xl font-bold text-ink-950 leading-snug mb-2 group-hover:text-brand-700 transition-colors">
                        {r.title}
                    </h3>
                    <div className="flex items-start gap-1.5 text-sm text-ink-500 font-medium mb-2">
                        <Building2 size={13} className="text-ink-400 mt-0.5 shrink-0" />
                        <span className="line-clamp-1">{r.organo || "—"}</span>
                    </div>
                    {(r.city || r.community_label || r.province) && (
                        <div className="flex items-center gap-1.5 text-xs text-ink-500 font-medium mb-2 flex-wrap">
                            <MapPin size={11} className="text-emerald-600 shrink-0" />
                            {r.city && (
                                <span className="font-bold text-ink-700">{r.city}</span>
                            )}
                            {r.province && r.province !== r.city && (
                                <span className="text-ink-400">· {r.province}</span>
                            )}
                            {r.community_label && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1">
                                    {r.community_label}
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center gap-4 text-sm">
                        <span className="font-mono font-bold text-ink-950">
                            {fmtEUR(r.budget)}
                        </span>
                        {r.deadline && (
                            <span className="inline-flex items-center gap-1 text-amber-700 font-bold">
                                <CalendarIcon size={12} />
                                {r.deadline}
                            </span>
                        )}
                        {r.link && (
                            <a
                                href={r.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-brand-600 font-bold hover:underline"
                                data-testid={`plcsp-link-${rowId}`}
                            >
                                Ver en PLCSP
                                <ExternalLink size={11} />
                            </a>
                        )}
                    </div>
                </div>
                <div className="shrink-0 flex flex-col gap-2 items-stretch">
                    {isImported ? (
                        <Button
                            disabled
                            className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 h-10 px-5 font-bold cursor-default hover:bg-emerald-50"
                            data-testid={`plcsp-imported-${rowId}`}
                        >
                            <CheckCircle2 size={14} className="mr-1.5" />
                            Importada
                        </Button>
                    ) : (
                        <>
                            {(() => {
                                const link = (r.link || "").toLowerCase();
                                const isExternal =
                                    !!r.link && !link.includes("contrataciondelestado.es");
                                return (
                                    <>
                                        <Button
                                            onClick={() => onImport(r, true)}
                                            disabled={isImporting}
                                            className="rounded-full bg-brand-600 hover:bg-brand-700 text-white h-10 px-5 font-bold shadow-pop hover:scale-[1.03] transition-all whitespace-nowrap"
                                            data-testid={`plcsp-import-pdfs-${rowId}`}
                                            title={
                                                isExternal
                                                    ? "Importa metadatos y abre la plataforma autonómica/local para descargar los pliegos manualmente"
                                                    : "Crear licitación y descargar pliegos PDF"
                                            }
                                        >
                                            {isImporting &&
                                            importingWithPdfs === r.external_id ? (
                                                <>
                                                    <Loader2
                                                        size={13}
                                                        className="animate-spin mr-1.5"
                                                    />
                                                    Descargando…
                                                </>
                                            ) : isExternal ? (
                                                <>
                                                    <DownloadCloud
                                                        size={13}
                                                        className="mr-1.5"
                                                    />
                                                    Importar con pliegos
                                                </>
                                            ) : (
                                                <>
                                                    <DownloadCloud
                                                        size={13}
                                                        className="mr-1.5"
                                                    />
                                                    Importar con pliegos
                                                </>
                                            )}
                                        </Button>
                                        {isExternal && (
                                            <span
                                                className="text-[10px] font-bold uppercase tracking-wider text-amber-700 text-center"
                                                data-testid={`plcsp-external-hint-${rowId}`}
                                            >
                                                Plataforma autonómica · puede tardar 30-60 s
                                            </span>
                                        )}
                                        <button
                                            onClick={() => onImport(r, false)}
                                            disabled={isImporting}
                                            className="text-xs font-bold text-ink-500 hover:text-brand-600 transition-colors text-center"
                                            data-testid={`plcsp-import-${rowId}`}
                                        >
                                            {isImporting && importingWithPdfs !== r.external_id
                                                ? "Importando…"
                                                : "Solo metadatos"}
                                        </button>
                                    </>
                                );
                            })()}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
